/**
 * Gössl precise search (#912).
 *
 * Shopify's storefront search adds everything it finds "by meaning": "lederhose" returns 673
 * products, 26 of them Lederhosen. The goessl-search worker says which products a query really
 * names (catalogue words first, bge-m3 neighbours second). This script keeps those of the native
 * hits — in Shopify's order, so sorting and filters still apply — appends the ones the native
 * search missed (relevance order, no filters only), and redraws count, grid and pagination.
 *
 * Fail-open: nothing on the page changes until every piece has arrived; any error, timeout or a
 * "bypass" answer leaves the native results exactly as they are.
 */
(function () {
  'use strict';

  var cfgEl = document.querySelector('[data-sp-config]');
  var grid = document.querySelector('[data-sp-grid]');
  if (!cfgEl || !grid || !window.fetch || !window.URL || !window.DOMParser) return;

  var cfg;
  try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { return; }
  var terms = String(cfg.terms || '').trim();
  if (!terms || !cfg.endpoint || terms.indexOf('g26sp') === 0) return;

  var TIMEOUT_MS = 4000;
  var CARDS_PER_RENDER = 20; // all_products resolves at most 20 handles per render
  var endpoint = String(cfg.endpoint).replace(/\/+$/, '');
  var here = new URL(window.location.href);
  var perPage = Number(cfg.perPage) || 24;
  var page = Math.max(1, parseInt(here.searchParams.get('page') || '1', 10) || 1);
  var hasFilters = false;
  here.searchParams.forEach(function (v, k) { if (k.indexOf('filter.') === 0) hasFilters = true; });
  var union = Boolean(cfg.union) && !hasFilters && (cfg.sort || 'relevance') === 'relevance';

  function timed(url, init) {
    var ctrl = new AbortController();
    var timer = setTimeout(function () { ctrl.abort(); }, TIMEOUT_MS);
    return fetch(url, Object.assign({ signal: ctrl.signal }, init || {}))
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status + ' ' + url); return r.text(); })
      .finally(function () { clearTimeout(timer); });
  }
  var json = function (url, init) { return timed(url, init).then(JSON.parse); };

  // The native hits, in the order and with the filters of this page: ?<same params>&view=sp-handles,
  // 250 per page. The page already knows how many there are, so every page is asked for at once.
  function nativeHandles() {
    var u = new URL(here.href);
    u.searchParams.delete('page');
    u.searchParams.set('view', 'sp-handles');
    var pages = Math.min(Math.max(1, Math.ceil((Number(cfg.nativeCount) || 1) / 250)), 8);
    var requests = [];
    for (var p = 1; p <= pages; p++) {
      var up = new URL(u.href);
      if (p > 1) up.searchParams.set('page', String(p));
      requests.push(json(up.href, { credentials: 'same-origin' }));
    }
    return Promise.all(requests).then(function (parts) {
      var seen = new Set();
      var handles = parts.reduce(function (all, part) { return all.concat(part.handles || []); }, [])
        .filter(function (h) { if (!h || seen.has(h)) return false; seen.add(h); return true; });
      return { handles: handles, sid: sidOf(parts[0] && parts[0].firstUrl) };
    });
  }

  // Section Rendering API wants the rendered id of a JSON-template section (template--…__precision-cards).
  function cardsSectionId() {
    var el = document.querySelector('[id^="shopify-section-"][id$="__' + (cfg.cardsSection || 'precision-cards') + '"]');
    return el ? el.id.slice('shopify-section-'.length) : null;
  }

  // Cards for handles the page does not show yet, rendered by the theme's own card snippet.
  function renderCards(handles) {
    var sectionId = cardsSectionId();
    if (!sectionId) return Promise.reject(new Error('cards section missing from the search template'));
    var chunks = [];
    for (var i = 0; i < handles.length; i += CARDS_PER_RENDER) chunks.push(handles.slice(i, i + CARDS_PER_RENDER));
    return Promise.all(chunks.map(function (chunk) {
      var u = new URL(window.location.origin + window.location.pathname);
      u.searchParams.set('q', 'g26sp ' + chunk.join(' '));
      u.searchParams.set('section_id', sectionId);
      return timed(u.href, { credentials: 'same-origin' });
    })).then(function (htmls) {
      var cards = new Map();
      htmls.forEach(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        doc.querySelectorAll('[data-sp-handle]').forEach(function (li) { cards.set(li.getAttribute('data-sp-handle'), document.importNode(li, true)); });
      });
      return cards;
    });
  }

  function countText(n) {
    var tpl = n === 1 ? cfg.count && cfg.count.one : cfg.count && cfg.count.other;
    if (!tpl) return null;
    if (n === 1) return tpl;
    return tpl.indexOf('987654') >= 0 ? tpl.replace('987654', String(n)) : null;
  }

  function pageUrl(p) {
    var u = new URL(here.href);
    if (p > 1) u.searchParams.set('page', String(p)); else u.searchParams.delete('page');
    return u.pathname + u.search;
  }

  // Same markup as the section's own pagination, so the theme's styles apply unchanged.
  function pagination(current, pages) {
    var nav = document.createElement('nav');
    nav.className = 'g26-pagination';
    nav.setAttribute('aria-label', (cfg.labels && cfg.labels.pages) || '');
    nav.setAttribute('data-sp-pagination', '');
    function link(p, cls, text, label) {
      var a = document.createElement('a');
      a.href = pageUrl(p);
      a.className = cls;
      a.textContent = text;
      if (label) a.setAttribute('aria-label', label);
      nav.appendChild(a);
    }
    function span(cls, text, isCurrent) {
      var s = document.createElement('span');
      s.className = cls;
      s.textContent = text;
      if (isCurrent) s.setAttribute('aria-current', 'page');
      nav.appendChild(s);
    }
    if (current > 1) link(current - 1, 'g26-pagination__arrow', '\u2190', cfg.labels && cfg.labels.previous);
    var shown = {};
    [1, current - 1, current, current + 1, pages].forEach(function (p) { if (p >= 1 && p <= pages) shown[p] = true; });
    var last = 0;
    Object.keys(shown).map(Number).sort(function (a, b) { return a - b; }).forEach(function (p) {
      if (last && p > last + 1) span('g26-pagination__gap', '…');
      if (p === current) span('g26-pagination__page is-current', String(p), true);
      else link(p, 'g26-pagination__page', String(p));
      last = p;
    });
    if (current < pages) link(current + 1, 'g26-pagination__arrow', '\u2192', cfg.labels && cfg.labels.next);
    return nav;
  }

  // Shopify attributes search clicks through ?_pos=&_sid=&_ss=r on result links: keep it truthful.
  // The search id comes from a native card on the page, or (a page beyond the native ones) from the
  // first native hit of the same search.
  function sidOf(href) {
    if (!href) return null;
    try { return new URL(href, window.location.origin).searchParams.get('_sid'); } catch (e) { return null; }
  }
  function searchId(fallback) {
    var a = grid.querySelector('a[href*="_sid="]');
    return (a && sidOf(a.getAttribute('href'))) || fallback || null;
  }
  function attribute(li, pos, sid) {
    if (!sid) return;
    li.querySelectorAll('a[href*="/products/"]').forEach(function (a) {
      var u = new URL(a.getAttribute('href'), window.location.origin);
      u.searchParams.set('_pos', String(pos));
      u.searchParams.set('_sid', sid);
      u.searchParams.set('_ss', 'r');
      a.setAttribute('href', u.pathname + u.search);
    });
  }

  function run() {
    grid.setAttribute('aria-busy', 'true');
    var q = '?q=' + encodeURIComponent(terms) + '&locale=' + encodeURIComponent(cfg.locale || 'de');
    Promise.all([
      json(endpoint + '/v1/relevant' + q, { credentials: 'omit' }),
      json(endpoint + '/v1/known', { credentials: 'omit' }),
      nativeHandles(),
    ]).then(function (res) {
      var relevant = res[0], known = new Set(res[1].handles || []), native = res[2].handles, nativeSid = res[2].sid;
      if (!relevant || relevant.mode !== 'filter' || !Array.isArray(relevant.handles)) return null;
      var keep = new Set(relevant.handles);
      var nativeSet = new Set(native);
      // products the index does not know yet (new since the last hourly rebuild) always stay
      var list = native.filter(function (h) { return keep.has(h) || !known.has(h); });
      if (union) list = list.concat(relevant.handles.filter(function (h) { return !nativeSet.has(h); }));
      if (!list.length) return null;
      if (list.length === native.length && list.every(function (h, i) { return h === native[i]; })) return null;

      var pages = Math.max(1, Math.ceil(list.length / perPage));
      var current = Math.min(page, pages);
      var slice = list.slice((current - 1) * perPage, current * perPage);
      var onScreen = new Map();
      grid.querySelectorAll('[data-handle]').forEach(function (card) {
        var li = card.closest('li');
        if (li) onScreen.set(card.getAttribute('data-handle'), li);
      });
      var missing = slice.filter(function (h) { return !onScreen.has(h); });
      return (missing.length ? renderCards(missing) : Promise.resolve(new Map())).then(function (rendered) {
        return { list: list, slice: slice, pages: pages, current: current, onScreen: onScreen, rendered: rendered, sid: nativeSid };
      });
    }).then(function (plan) {
      if (!plan) return;
      var sid = searchId(plan.sid);
      var items = plan.slice.map(function (h) { return plan.onScreen.get(h) || plan.rendered.get(h); }).filter(Boolean);
      if (!items.length) return;
      // all pieces are here: one swap, nothing half-applied
      var frag = document.createDocumentFragment();
      items.forEach(function (li, i) { attribute(li, (plan.current - 1) * perPage + i + 1, sid); frag.appendChild(li); });
      grid.textContent = '';
      grid.appendChild(frag);

      var countEl = document.querySelector('[data-sp-count]');
      var text = countText(plan.list.length);
      if (countEl && text) countEl.textContent = text;

      var oldNav = document.querySelector('[data-sp-pagination]');
      var nav = plan.pages > 1 ? pagination(plan.current, plan.pages) : null;
      if (oldNav && nav) oldNav.replaceWith(nav);
      else if (oldNav) oldNav.remove();
      else if (nav) grid.insertAdjacentElement('afterend', nav);

      if (window.Goessl && window.Goessl.wishlist && window.Goessl.wishlist.updateUI) window.Goessl.wishlist.updateUI();
      window.dispatchEvent(new Event('resize')); // card-product.js re-checks swatch overflow
      grid.setAttribute('data-sp-applied', String(plan.list.length));
    }).catch(function (err) {
      if (window.console) console.warn('[search-precision] native results kept:', err && err.message ? err.message : err);
    }).then(function () {
      grid.removeAttribute('aria-busy');
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
})();
